import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, At, CheckCircle, XCircle, CircleNotch, User as UserIcon, MapPin, Translate, Phone } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { COUNTRIES, LANGUAGES, flagOf } from "@/lib/catalog";

export default function ChooseUsernamePage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [username, setUsername] = useState(user?.username || "");
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [country, setCountry] = useState((user?.country || "").toUpperCase());
  const [city, setCity] = useState(user?.city || "");
  const [language, setLanguage] = useState((user?.language || "").toLowerCase());
  const [phone, setPhone] = useState(user?.phone || "");
  const [check, setCheck] = useState({ status: "idle", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  // Best-effort: pre-fill language from browser if unset.
  useEffect(() => {
    if (language) return;
    const nav = (navigator.language || "en").toLowerCase().split("-")[0];
    if (LANGUAGES.some((l) => l.code === nav)) setLanguage(nav);
  }, [language]);

  const normalized = useMemo(() => username.trim().replace(/^@/, "").toLowerCase(), [username]);
  const localValid = /^[a-zA-Z0-9_]{3,20}$/.test(normalized);

  useEffect(() => {
    if (!normalized) { setCheck({ status: "idle", reason: "" }); return; }
    if (!localValid) { setCheck({ status: "invalid", reason: "3–20 chars: letters, numbers, underscore." }); return; }
    setCheck({ status: "checking", reason: "" });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/username-available`, { params: { username: normalized } });
        setCheck({ status: data.available ? "available" : "taken", reason: data.reason || "" });
      } catch {
        setCheck({ status: "idle", reason: "" });
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [normalized, localValid]);

  const canSubmit = check.status === "available" && firstName.trim().length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/choose-username", {
        username: normalized,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        country: country || "",
        city: city.trim(),
        language: language || "",
        phone: phone.trim(),
      });
      setUser(data);
      toast.success(`You're @${data.username}`);
      navigate(data.onboarded ? (data.verified ? "/feed" : "/verify") : "/onboarding", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="choose-username-page">
      <div className="mx-auto max-w-md min-h-screen sm:border-x sm:border-white/5 px-6 py-10">
        <div className="mb-8"><Logo size={40} /></div>

        <h1 className="font-heading text-4xl font-black tracking-tight">
          Pick your<br /><span className="text-primary">handle.</span>
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          This is how friends find you on Voxly. Add your location and language so we can group you with people who get you.
        </p>

        <div className="mt-8 space-y-4">
          {/* Username */}
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Username</span>
            <div className="mt-1.5 relative">
              <At size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                maxLength={20}
                autoComplete="off"
                placeholder="yourhandle"
                className="w-full bg-secondary border border-white/10 rounded-xl pl-9 pr-10 py-3 text-base focus:outline-none focus:border-primary"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2">
                {check.status === "checking" && <CircleNotch size={18} className="animate-spin text-muted-foreground" />}
                {check.status === "available" && <CheckCircle size={20} weight="fill" className="text-emerald-500" data-testid="username-available-icon" />}
                {(check.status === "taken" || check.status === "invalid") && <XCircle size={20} weight="fill" className="text-destructive" data-testid="username-taken-icon" />}
              </span>
            </div>
            <div className="mt-1.5 text-xs min-h-[1rem]" data-testid="username-status">
              {check.status === "available" && <span className="text-emerald-500">@{normalized} is available 🎉</span>}
              {check.status === "taken" && <span className="text-destructive">{check.reason}</span>}
              {check.status === "invalid" && <span className="text-destructive">{check.reason}</span>}
            </div>
          </label>

          {/* First + Last name */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">First name</span>
              <div className="mt-1.5 relative">
                <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="first-name-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={40}
                  placeholder="Alex"
                  className="w-full bg-secondary border border-white/10 rounded-xl pl-9 pr-3 py-3 text-base focus:outline-none focus:border-primary"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Last name</span>
              <input
                data-testid="last-name-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                maxLength={40}
                placeholder="Optional"
                className="mt-1.5 w-full bg-secondary border border-white/10 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary"
              />
            </label>
          </div>

          {/* Country + City */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Country</span>
              <div className="mt-1.5 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base select-none" aria-hidden>
                  {country ? flagOf(country) : <MapPin size={16} className="text-muted-foreground" />}
                </span>
                <select
                  data-testid="country-select"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full appearance-none bg-secondary border border-white/10 rounded-xl pl-10 pr-3 py-3 text-base focus:outline-none focus:border-primary"
                >
                  <option value="">Select…</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{flagOf(c.code)}  {c.name}</option>
                  ))}
                </select>
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">City</span>
              <input
                data-testid="city-input"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                maxLength={80}
                placeholder="Optional"
                className="mt-1.5 w-full bg-secondary border border-white/10 rounded-xl px-3 py-3 text-base focus:outline-none focus:border-primary"
              />
            </label>
          </div>

          {/* Language */}
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Primary language</span>
            <div className="mt-1.5 relative">
              <Translate size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <select
                data-testid="language-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full appearance-none bg-secondary border border-white/10 rounded-xl pl-9 pr-3 py-3 text-base focus:outline-none focus:border-primary"
              >
                <option value="">Select…</option>
                {LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>{l.name}</option>
                ))}
              </select>
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">We'll surface people who speak this language on Discover.</p>
          </label>

          {/* Phone */}
          <label className="block">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Phone <span className="opacity-60">(optional)</span></span>
            <div className="mt-1.5 relative">
              <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="phone-input"
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={20}
                placeholder="(314) 555 4567"
                className="w-full bg-secondary border border-white/10 rounded-xl pl-9 pr-3 py-3 text-base focus:outline-none focus:border-primary"
              />
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">Used later for account recovery & SMS login (when enabled).</p>
          </label>
        </div>

        <button
          data-testid="choose-username-submit"
          onClick={submit}
          disabled={!canSubmit}
          className="mt-10 w-full rounded-full bg-primary text-primary-foreground py-4 text-base font-semibold flex items-center justify-center gap-2 glow-hover disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? "Saving…" : <>Continue <ArrowRight size={18} weight="bold" /></>}
        </button>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          You can change your display name, location, and language later, but your handle is permanent.
        </p>
      </div>
    </div>
  );
}
